import { findAttribute } from 'app/utils/classification/classificationUtils';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { NUMBER_OPERATORS, STRING_OPERATORS } from './GlobalSearchModal.constants';

const typeFilterConfig = {
    key: 'type',
    text: 'Type',
    icon: 'file-document-outline',
    skipOperator: true,
    
    getValues: (context) => {
        const { entityTypes } = context.props;
        return entityTypes || [];
    },
    
    filterValues: (values, query) => {
        if (!query.trim()) {
            return values.slice(0, 50);
        }

        const queryLower = query.toLowerCase();

        const filtered = values.filter((type) => {
            const name = (type.name || '').toLowerCase();
            const displayName = (type.displayName || '').toLowerCase();
            const label = (type.label || '').toLowerCase();
            const uri = (type.uri || '').toLowerCase();
            const id = (type.id || '').toLowerCase();

            return (
                name.includes(queryLower) ||
                displayName.includes(queryLower) ||
                label.includes(queryLower) ||
                uri.includes(queryLower) ||
                id.includes(queryLower)
            );
        });

        return filtered.slice(0, 50);
    },
    
    formatDisplayValue: (value) => {
        if (value && typeof value === 'object' && value.displayName) {
            return value.displayName;
        }
        return value;
    },
    
    formatSelectedValue: (type) => {
        return { type, displayName: type.name };
    },
    
    getListItemProps: (value) => {
        return {
            icon: value.icon || 'file-document-outline',
            iconType: value.iconType || 'mdi',
            iconColor: value.color,
            primary: value.name,
            secondary: value.id ? `#${value.id.slice(0, 8)}` : '',
        };
    },
};

/** Filter keys that use UUID values (user IDs). */
export const UUID_FILTER_KEYS = ['createdBy', 'modifiedBy'];

/** Operator symbols for UUID-based filters (createdBy, modifiedBy, etc.). Single-value only. */
export const UUID_FILTER_OPERATORS = ['='];

export const createUserFilterConfig = (key, text, icon) => ({
    key,
    text,
    icon,
    allowedOperators: UUID_FILTER_OPERATORS,

    getValues: (context) => {
        const { userReferences } = context.props;
        return userReferences || [];
    },
    
    filterValues: (values, query) => {
        if (!query.trim()) {
            return values.slice(0, 50);
        }

        const queryLower = query.toLowerCase();

        const filtered = values.filter((user) => {
            const name = (user.name || '').toLowerCase();
            const displayName = (user.displayName || '').toLowerCase();
            const username = (user.username || '').toLowerCase();
            const email = (user.email || '').toLowerCase();
            const id = (user.id || '').toLowerCase();

            return (
                name.includes(queryLower) ||
                displayName.includes(queryLower) ||
                username.includes(queryLower) ||
                email.includes(queryLower) ||
                id.includes(queryLower)
            );
        });

        return filtered.slice(0, 50);
    },
    
    formatDisplayValue: (value) => {
        if (value && typeof value === 'object' && value.displayName) {
            return value.displayName;
        }
        return value;
    },
    
    formatSelectedValue: (user) => {
        return { user, displayName: user.name || user.username };
    },
    
    getListItemProps: (value) => {
        const avatarSrc = value.image ? getAttachmentUrl(value.id, 'user', value.image) : null;
        return {
            isAvatar: true,
            avatarSrc,
            avatarName: value.name || value.username,
            primary: value.name || value.username,
            secondary: value.username ? `@${value.username}` : (value.id ? `#${value.id.slice(0, 8)}` : ''),
        };
    },
});

const createdByFilterConfig = createUserFilterConfig(
    'createdBy',
    'Created by',
    'account-plus-outline'
);

const modifiedByFilterConfig = createUserFilterConfig(
    'modifiedBy',
    'Modified by',
    'account-edit-outline'
);

const createdDateFilterConfig = {
    key: 'createdDate',
    text: 'Created date',
    icon: 'calendar-plus',
    skipOperator: true,
    isDateRange: true,
    
    getValues: () => [],
    filterValues: () => [],
    
    formatDisplayValue: (value) => {
        if (!value) return '';
        if (value.displayText) return value.displayText;
        return 'Date range selected';
    },
    
    formatSelectedValue: (value) => value,
    
    getListItemProps: () => ({
        icon: 'calendar-range',
        iconType: 'mdi',
        iconColor: '#4CAF50',
        primary: 'Select date range...',
    }),
};

const modifiedDateFilterConfig = {
    key: 'modifiedDate',
    text: 'Modified date',
    icon: 'calendar-edit',
    skipOperator: true,
    isDateRange: true,
    
    getValues: () => [],
    filterValues: () => [],
    
    formatDisplayValue: (value) => {
        if (!value) return '';
        if (value.displayText) return value.displayText;
        return 'Date range selected';
    },
    
    formatSelectedValue: (value) => value,
    
    getListItemProps: () => ({
        icon: 'calendar-clock',
        iconType: 'mdi',
        iconColor: '#FF9800',
        primary: 'Select date range...',
    }),
};

// Helper function to extract all entityPrimaryIndexes with state === 'created'
const getEntityPrimaryIndexesFilters = (entityTypes) => {
    if (!entityTypes || !entityTypes.length) {
        return {};
    }

    const filtersMap = {};
    
    // Iterate through all entity types
    entityTypes.forEach(entityType => {
        const entityPrimaryIndexes = entityType?.entityPrimaryIndexes || [];
        const entityTypeUri = entityType?.uri; // The actual dataType for the query
        const entityTypeName = entityType?.name; // The entity type name for display
        
        // Filter indexes with state === 'created'
        entityPrimaryIndexes
            .filter(index => index?.state === 'created' && index?.primaryAttribute)
            .forEach(index => {
                const { primaryAttribute, type: indexType } = index;
                const fieldKey = `primary.${primaryAttribute}`;
                
                // Skip if we've already processed this attribute
                if (filtersMap[fieldKey]) {
                    return;
                }
                
                // Label from form definition; type from backend (GraphQL has two types: number and string)
                const attribute = findAttribute(entityType?.formDefinition, primaryAttribute);
                const label = attribute?.properties?.label || primaryAttribute;
                const filterType = indexType === 'number' ? 'number' : 'string';
                const icon = filterType === 'number' ? 'numeric' : 'format-text';
                const allowedOperators = (filterType === 'number' ? NUMBER_OPERATORS : STRING_OPERATORS).map((op) => op.symbol);

                // Create filter config for this primary index
                filtersMap[fieldKey] = {
                    key: fieldKey,
                    text: label,
                    icon,
                    primaryAttribute,
                    filterType,
                    entityTypeUri, // Store the entity type uri for dataType in query
                    entityTypeName, // Store the entity type name for display
                    allowTextInput: true, // Allow free text input for these filters
                    allowedOperators,
                    
                    getValues: (context) => {
                        // Return empty array - these filters use direct text input
                        // No predefined values to select from
                        return [];
                    },
                    
                    filterValues: (values, query) => {
                        // Return empty array - user types directly into search input
                        return [];
                    },
                    
                    formatDisplayValue: (value) => {
                        if (value && typeof value === 'object' && value.displayText) {
                            return value.displayText;
                        }
                        if (value && typeof value === 'object' && value.value !== undefined) {
                            return String(value.value);
                        }
                        return value ? String(value) : '';
                    },
                    
                    formatSelectedValue: (value) => {
                        // Return the value as-is, or format it if needed
                        return typeof value === 'string' ? value : (value?.value || value);
                    },
                    
                    getListItemProps: (value) => {
                        // This shouldn't be called since we return empty array
                        // But keeping it for safety
                        return {
                            icon: icon,
                            iconType: 'mdi',
                            iconColor: '#2196F3',
                            primary: String(value || ''),
                        };
                    },
                };
            });
    });
    
    return filtersMap;
};

/** Filter keys that require a Type token to be selected before they become available. */
export const TYPE_DEPENDENT_FILTER_KEYS = ['createdBy', 'modifiedBy', 'createdDate', 'modifiedDate'];

/** Static filter keys (always shown). Dynamic primary.* filters are restricted by dataType when set. */
export const STATIC_FILTER_KEYS = [
    'type',
    ...TYPE_DEPENDENT_FILTER_KEYS,
];

// Base filter registry with static filters
const BASE_FILTER_REGISTRY = {
    type: typeFilterConfig,
    createdBy: createdByFilterConfig,
    modifiedBy: modifiedByFilterConfig,
    createdDate: createdDateFilterConfig,
    modifiedDate: modifiedDateFilterConfig,
};

// Dynamic registry that will be populated with entityPrimaryIndexes filters
let dynamicFilterRegistry = {};

// Function to rebuild the filter registry with entity types
export const rebuildFilterRegistry = (entityTypes) => {
    dynamicFilterRegistry = getEntityPrimaryIndexesFilters(entityTypes || []);
};

// Combined filter registry
export const getFilterRegistry = () => {
    return { ...BASE_FILTER_REGISTRY, ...dynamicFilterRegistry };
};

export const FILTER_REGISTRY = new Proxy({}, {
    get(target, prop) {
        const registry = getFilterRegistry();
        return registry[prop];
    },
    ownKeys(target) {
        const registry = getFilterRegistry();
        return Object.keys(registry);
    },
    getOwnPropertyDescriptor(target, prop) {
        const registry = getFilterRegistry();
        return Object.getOwnPropertyDescriptor(registry, prop) || Object.getOwnPropertyDescriptor(target, prop);
    }
});

export const getFilterConfig = (key) => {
    const registry = getFilterRegistry();
    return registry[key] || null;
};

export const getAllFilterOptions = (hasTypeToken) => {
    const registry = getFilterRegistry();
    return Object.values(registry).map(config => {
        let displayText = config.text;
        let subtitle = null;
        // For primary.* filters, append entity type name in brackets
        if (config.key.startsWith('primary.') && config.entityTypeName) {
            displayText = `${config.text} (${config.entityTypeName})`;
        }
        // For Type filter, show subtitle hint when no type is selected yet
        if (config.key === 'type' && !hasTypeToken) {
            subtitle = 'Includes additional filters';
        }
        return {
            key: config.key,
            text: displayText,
            subtitle,
            icon: config.icon,
        };
    });
};

